import { ErrorMessage } from '@/components/Message/ErrorMessage';
import { ChangeEvent, forwardRef, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface RadioItem {
  displayedText: string;
  value: string;
  textClassName?: string;
  children?: React.ReactNode;
}

interface RadioProps
  extends React.PropsWithRef<JSX.IntrinsicElements['input']> {
  items: RadioItem[];
  defaultValue?: string;
  errorMessage?: string;
  containerClassName?: string;
  radioListClassName?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      items,
      defaultValue = '',
      value,
      errorMessage,
      containerClassName,
      radioListClassName,
      onChange,
      ...rest
    },
    ref
  ) => {
    const [checkedValue, setCheckedValue] = useState(defaultValue);

    useEffect(() => {
      setCheckedValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
      if (value !== undefined) {
        setCheckedValue(value as string);
      }
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setCheckedValue(e.target.value);

      if (onChange) {
        onChange(e);
      }
    };

    const getRadioItem = (item: RadioItem) => {
      const { displayedText, value, children } = item;

      return (
        <div key={value} className="radio-item-wrapper">
          <label className="inline-flex items-center gap-2 hover:cursor-pointer">
            <input
              ref={ref}
              className="appearance-none w-[17px] h-[17px] rounded-full ring-1 border-[0.5px] ring-gray-200 border-gray-300 bg-gray-200 checked:ring-blue-396AFC checked:border-[2.5px] checked:border-white checked:bg-gradient-blue"
              type="radio"
              checked={value === checkedValue}
              value={value}
              onChange={handleChange}
              {...rest}
            />
            <span
              className={twMerge(
                'text-gray-700 text-sm'
                // value === checkedValue && 'text-blue-396AFC',
              )}
            >
              {displayedText}
            </span>
          </label>
          {children && <div className="mt-3">{children}</div>}
        </div>
      );
    };

    const renderItems = () => {
      if (items.length > 0) {
        return items.map((item) => {
          return getRadioItem(item);
        });
      }

      if (items.length === 1) {
        return getRadioItem(items[0] as RadioItem);
      }

      return <></>;
    };

    return (
      <div className={twMerge('flex flex-col', containerClassName)}>
        <div
          className={twMerge(
            'flex flex-col items-start gap-4',
            radioListClassName
          )}
        >
          {renderItems()}
        </div>
        {errorMessage && (
          <ErrorMessage className="-mt-0.75" message={errorMessage} />
        )}
      </div>
    );
  }
);
